import { useEffect, useRef, useState } from "react";
import { Copy, FileText, Film, Trash2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError, photoSrc } from "@/lib/api";

const fmtSize = (n) =>
  n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`;

const isImage = (m) => (m.content_type || "").startsWith("image/") && !(m.content_type || "").includes("svg");
const isVideo = (m) => (m.content_type || "").startsWith("video/");

export const MediaTab = () => {
  const [files, setFiles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const inputRef = useRef(null);

  const load = () => {
    api.get("/media").then((r) => setFiles(r.data)).catch(() => {});
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const pick = (list) => {
    const arr = Array.from(list || []).slice(0, 20);
    if (arr.length) setQueue(arr);
  };

  const upload = async () => {
    if (!queue.length) {
      toast.error("Choose files first.");
      return;
    }
    setUploading(true);
    try {
      for (let i = 0; i < queue.length; i++) {
        setProgress(`Uploading ${i + 1}/${queue.length}…`);
        const fd = new FormData();
        fd.append("file", queue[i]);
        await api.post("/media/upload", fd);
      }
      toast.success(queue.length === 1 ? "File uploaded." : `${queue.length} files uploaded.`);
      setQueue([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      setProgress("");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Link copied to clipboard."))
      .catch(() => toast.error("Could not copy link."));
  };

  const removeFile = async (m) => {
    if (!window.confirm(`Delete "${m.original_filename}"?`)) return;
    try {
      await api.delete(`/media/${m.id}`);
      toast.success("File deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removePhoto = async (p) => {
    if (!window.confirm(`Remove "${p.title}" from the Photo Library? This also removes it from the public site.`)) return;
    try {
      await api.delete(`/photos/${p.id}`);
      toast.success("Photo removed from the library.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const items = [
    ...files.map((m) => ({ kind: "file", data: m, created_at: m.created_at })),
    ...photos.map((p) => ({ kind: "photo", data: p, created_at: p.created_at })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div data-testid="admin-media-tab">
      <div className="border border-white/10 bg-[#12151C] p-5 mb-8">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-4">
          Upload files & media — images, video, PDF · max 25MB each
        </p>
        <div
          data-testid="media-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pick(e.dataTransfer.files);
          }}
          className="cursor-pointer border border-dashed border-white/20 hover:border-[#FF3B30]/70 transition-colors bg-[#0A0C10] p-6 flex items-center gap-5"
        >
          <UploadCloud size={28} className="text-slate-500 shrink-0" />
          <div>
            <p className="text-sm text-slate-300">
              {queue.length === 0 && "Drop files or click to browse — up to 20 at once"}
              {queue.length === 1 && queue[0].name}
              {queue.length > 1 && `${queue.length} files selected`}
            </p>
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-600 mt-1">
              Stored in object storage · every file gets a shareable link
            </p>
          </div>
          <input
            ref={inputRef}
            data-testid="media-upload-input"
            type="file"
            multiple
            className="hidden"
            onChange={(e) => pick(e.target.files)}
          />
        </div>

        {queue.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="media-queue">
            {queue.map((f, i) => (
              <div key={`${f.name}-${i}`} data-testid={`media-queue-${i}`} className="flex items-center gap-3 border border-white/10 px-3 py-2">
                <FileText size={14} className="text-slate-500 shrink-0" />
                <span className="text-xs text-slate-300 truncate flex-1">{f.name}</span>
                <span className="text-[10px] font-mono text-slate-600 shrink-0">{fmtSize(f.size)}</span>
                <button
                  type="button"
                  data-testid={`media-queue-remove-${i}`}
                  onClick={() => setQueue((prev) => prev.filter((_, j) => j !== i))}
                  className="text-slate-500 hover:text-[#FF3B30] transition-colors shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              data-testid="media-upload-btn"
              onClick={upload}
              disabled={uploading}
              className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-3.5 transition-colors mt-3"
            >
              {uploading ? progress || "Working…" : queue.length > 1 ? `Upload ${queue.length} Files` : "Upload"}
            </button>
          </div>
        )}
      </div>

      <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-4">
        {files.length} files stored · {photos.length} library frames — all linked in one place
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((item) => {
          const m = item.data;
          const isPhoto = item.kind === "photo";
          const url = isPhoto ? photoSrc(m) : `${API}/files/${m.storage_path}`;
          return (
            <div key={`${item.kind}-${m.id}`} data-testid={`media-card-${item.kind}-${m.id}`} className="border border-white/10 bg-[#12151C] overflow-hidden">
              {isPhoto || isImage(m) ? (
                <img src={url} alt={isPhoto ? m.title : m.original_filename} className="h-28 w-full object-cover" loading="lazy" />
              ) : (
                <div className="h-28 w-full flex items-center justify-center bg-[#0A0C10]">
                  {isVideo(m) ? <Film size={26} className="text-slate-600" /> : <FileText size={26} className="text-slate-600" />}
                </div>
              )}
              <div className="p-3">
                <p className="text-xs text-slate-200 truncate" title={isPhoto ? m.title : m.original_filename}>
                  {isPhoto ? m.title : m.original_filename}
                </p>
                <p className="text-[9px] font-mono uppercase tracking-[0.18em] mt-1">
                  {isPhoto ? (
                    <span className="text-amber-400">Library{m.album ? ` · ${m.album}` : ""}</span>
                  ) : (
                    <span className="text-slate-500">{m.content_type} · {fmtSize(m.size)}</span>
                  )}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid={`media-copy-${item.kind}-${m.id}`}
                    onClick={() => copyLink(url)}
                    className="flex-1 flex items-center justify-center gap-1.5 border border-white/15 hover:border-amber-400 hover:text-amber-400 text-slate-300 text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1.5 transition-colors"
                  >
                    <Copy size={12} /> Copy link
                  </button>
                  <button
                    data-testid={`media-delete-${item.kind}-${m.id}`}
                    onClick={() => (isPhoto ? removePhoto(m) : removeFile(m))}
                    className="p-1.5 text-slate-500 hover:text-[#FF3B30] transition-colors shrink-0"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="col-span-full border border-white/10 bg-[#12151C] p-10 text-center text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
            Nothing stored yet — upload your first asset above.
          </p>
        )}
      </div>
    </div>
  );
};
